import clsx from 'clsx';
import React, {type JSX} from 'react';
import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from 'react';
import styles from './ProjectSwitcher.module.scss';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface ProjectInfo {
  id: string;
  name: string;
  updatedAt: string;
}

interface ProjectListResponse {
  activeProject: string | null;
  projects: ProjectInfo[];
}

// ---------------------------------------------------------------------------
// Inline Icons
// ---------------------------------------------------------------------------

function ChevronIcon({open}: {open: boolean}) {
  return (
    <svg
      className={clsx(styles.triggerChevron, open && styles.open)}
      viewBox="0 0 24 24"
      fill="currentColor"
    >
      <path d="M7.41 8.59L12 13.17l4.59-4.58L18 10l-6 6-6-6z" />
    </svg>
  );
}

function PlusIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="currentColor">
      <path d="M19 13h-6v6h-2v-6H5v-2h6V5h2v6h6v2z" />
    </svg>
  );
}

function PencilIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="currentColor">
      <path d="M3 17.25V21h3.75L17.81 9.94l-3.75-3.75L3 17.25zM20.71 7.04a1.003 1.003 0 000-1.42l-2.34-2.34a1.003 1.003 0 00-1.42 0l-1.83 1.83 3.75 3.75 1.84-1.82z" />
    </svg>
  );
}

function TrashIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="currentColor">
      <path d="M6 19c0 1.1.9 2 2 2h8c1.1 0 2-.9 2-2V7H6v12zM19 4h-3.5l-1-1h-5l-1 1H5v2h14V4z" />
    </svg>
  );
}

function CheckIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="currentColor">
      <path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41L9 16.17z" />
    </svg>
  );
}

// ---------------------------------------------------------------------------
// ProjectRow
// ---------------------------------------------------------------------------

interface ProjectRowProps {
  project: ProjectInfo;
  isActive: boolean;
  isRenaming: boolean;
  renameValue: string;
  isLoading: boolean;
  onLoad: () => void;
  onStartRename: () => void;
  onRenameChange: (value: string) => void;
  onRenameSubmit: () => void;
  onCancelRename: () => void;
  onDelete: () => void;
}

function ProjectRow({
  project,
  isActive,
  isRenaming,
  renameValue,
  isLoading,
  onLoad,
  onStartRename,
  onRenameChange,
  onRenameSubmit,
  onCancelRename,
  onDelete,
}: ProjectRowProps) {
  const inputRef = useRef<HTMLInputElement>(null);

  useLayoutEffect(() => {
    if (isRenaming && inputRef.current) {
      inputRef.current.focus();
      inputRef.current.select();
    }
  }, [isRenaming]);

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (e.key === 'Enter') {
        e.preventDefault();
        onRenameSubmit();
      } else if (e.key === 'Escape') {
        e.preventDefault();
        onCancelRename();
      }
    },
    [onRenameSubmit, onCancelRename],
  );

  const handleRowClick = useCallback(() => {
    if (!isRenaming && !isActive) {
      onLoad();
    }
  }, [isRenaming, isActive, onLoad]);

  const stopPropagation = useCallback(
    (e: React.MouseEvent<HTMLElement>) => {
      e.stopPropagation();
    },
    [],
  );

  return (
    <div
      className={clsx(
        styles.projectRow,
        isActive && styles.active,
        isLoading && styles.loading,
      )}
      onClick={handleRowClick}
    >
      {isActive ? (
        <span className={styles.activeIndicator} />
      ) : (
        <span className={styles.inactiveIndicator} />
      )}

      {isRenaming ? (
        <input
          ref={inputRef}
          className={styles.renameInput}
          value={renameValue}
          onChange={e =>
            onRenameChange((e.target as HTMLInputElement).value)
          }
          onKeyDown={handleKeyDown}
          onBlur={onRenameSubmit}
          onClick={stopPropagation}
        />
      ) : (
        <span className={styles.projectName}>{project.name}</span>
      )}

      {!isRenaming && (
        <span className={styles.projectActions}>
          <button
            className={styles.actionButton}
            title="Rename project"
            onClick={e => {
              e.stopPropagation();
              onStartRename();
            }}
          >
            <PencilIcon />
          </button>
          {!isActive && (
            <button
              className={clsx(styles.actionButton, styles.deleteButton)}
              title="Delete project"
              onClick={e => {
                e.stopPropagation();
                onDelete();
              }}
            >
              <TrashIcon />
            </button>
          )}
        </span>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// ProjectSwitcher
// ---------------------------------------------------------------------------

export function ProjectSwitcher() {
  const [projects, setProjects] = useState<ProjectInfo[]>([]);
  const [activeId, setActiveId] = useState<string | null>(null);
  const [isOpen, setIsOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [renaming, setRenaming] = useState<string | null>(null);
  const [renameValue, setRenameValue] = useState('');
  const [rect, setRect] = useState<DOMRect | null>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);

  // ---- API helpers --------------------------------------------------------

  const fetchProjects = useCallback(async () => {
    try {
      const res = await fetch('/__cascade/projects');
      if (!res.ok) return;
      const data: ProjectListResponse = await res.json();
      setProjects(data.projects);
      setActiveId(data.activeProject);
    } catch {
      // silently ignore -- endpoint may not exist yet
    }
  }, []);

  const loadProject = useCallback(
    async (id: string) => {
      if (id === activeId) return;
      setIsLoading(true);
      try {
        const res = await fetch(`/__cascade/projects/${id}/load`, {
          method: 'POST',
        });
        if (!res.ok) {
          setIsLoading(false);
          return;
        }
        // Reload after backend rewrites project.ts and swaps the active project.
        window.location.reload();
      } catch {
        setIsLoading(false);
      }
    },
    [activeId],
  );

  const createNewProject = useCallback(async () => {
    const name = window.prompt('New project name:');
    if (!name || !name.trim()) return;
    setIsLoading(true);
    try {
      const res = await fetch('/__cascade/projects', {
        method: 'POST',
        headers: {'Content-Type': 'application/json'},
        body: JSON.stringify({name: name.trim()}),
      });
      if (!res.ok) {
        setIsLoading(false);
        return;
      }
      // New project was created and set as active -- reload.
      window.location.reload();
    } catch {
      setIsLoading(false);
    }
  }, []);

  const renameProject = useCallback(
    async (id: string, name: string) => {
      const trimmed = name.trim();
      if (!trimmed) {
        setRenaming(null);
        return;
      }
      try {
        const res = await fetch(`/__cascade/projects/${id}`, {
          method: 'PUT',
          headers: {'Content-Type': 'application/json'},
          body: JSON.stringify({name: trimmed}),
        });
        if (res.ok) {
          await fetchProjects();
        }
      } catch {
        // ignore
      }
      setRenaming(null);
    },
    [fetchProjects],
  );

  const deleteProject = useCallback(
    async (id: string) => {
      if (id === activeId) return;
      const project = projects.find(p => p.id === id);
      const confirmed = window.confirm(
        `Delete project "${project?.name ?? id}"? This cannot be undone.`,
      );
      if (!confirmed) return;
      try {
        const res = await fetch(`/__cascade/projects/${id}`, {
          method: 'DELETE',
        });
        if (res.ok) {
          await fetchProjects();
        }
      } catch {
        // ignore
      }
    },
    [activeId, projects, fetchProjects],
  );

  // ---- Effects ------------------------------------------------------------

  // Fetch project list on mount.
  useEffect(() => {
    fetchProjects();
  }, [fetchProjects]);

  // Close dropdown on Escape.
  useEffect(() => {
    if (!isOpen) return;
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        setIsOpen(false);
        setRenaming(null);
      }
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isOpen]);

  // ---- Derived state ------------------------------------------------------

  const activeName =
    projects.find(p => p.id === activeId)?.name ?? 'No Project';

  // ---- Handlers -----------------------------------------------------------

  const handleTriggerClick = useCallback(() => {
    if (isOpen) {
      setIsOpen(false);
      setRenaming(null);
    } else {
      if (triggerRef.current) {
        setRect(triggerRef.current.getBoundingClientRect());
      }
      setIsOpen(true);
      // Refresh the list each time we open.
      fetchProjects();
    }
  }, [isOpen, fetchProjects]);

  // ---- Render -------------------------------------------------------------

  return (
    <div className={styles.root}>
      <button
        ref={triggerRef}
        className={styles.trigger}
        onClick={handleTriggerClick}
      >
        <span className={styles.triggerLabel}>{activeName}</span>
        <ChevronIcon open={isOpen} />
      </button>

      {isOpen && rect && (
        <>
          <div
            className={styles.backdrop}
            onClick={() => {
              setIsOpen(false);
              setRenaming(null);
            }}
          />
          <div
            className={styles.dropdown}
            style={{
              left: rect.left,
              top: rect.bottom + 4,
              minWidth: Math.max(rect.width, 200),
            }}
          >
            {projects.map(project => (
              <ProjectRow
                key={project.id}
                project={project}
                isActive={project.id === activeId}
                isRenaming={renaming === project.id}
                renameValue={renameValue}
                isLoading={isLoading}
                onLoad={() => loadProject(project.id)}
                onStartRename={() => {
                  setRenaming(project.id);
                  setRenameValue(project.name);
                }}
                onRenameChange={setRenameValue}
                onRenameSubmit={() => renameProject(project.id, renameValue)}
                onCancelRename={() => setRenaming(null)}
                onDelete={() => deleteProject(project.id)}
              />
            ))}

            <div className={styles.divider} />

            <button
              className={styles.newProjectButton}
              onClick={createNewProject}
            >
              <PlusIcon />
              New project
            </button>
          </div>
        </>
      )}
    </div>
  );
}
